import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Store, DollarSign, Receipt, Bell, Palette, Users as UsersIcon,
  Settings2, Save, Sun, Moon, Camera, Upload, X, Plus, Trash2,
  Edit3, Shield, Clock, Globe, Mail, Phone, MapPin, Hash,
  Percent, Printer, FileText, Check, AlertTriangle, Database,
  Activity, Package, ShoppingCart, CreditCard, UserPlus, Ban
} from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Badge from '../components/common/Badge';
import Skeleton from '../components/common/Skeleton';
import StatCard from '../components/common/StatCard';
import SettingRow from '../components/settings/SettingRow';
import SettingsSection from '../components/settings/SettingsSection';
import {
  fetchSettings, updateSetting, fetchShopInfo, updateShop,
  updateFinancial, updateReceipt, updateNotifications, updateAppearance,
  uploadLogo, fetchSystemInfo
} from '../redux/slices/settingSlice';
import { setDarkMode, setFontSize, setTheme } from '../redux/slices/uiSlice';
import { getUsers, deleteUser, updateUser } from '../redux/slices/authSlice';

const tabs = [
  { id: 'shop', label: 'Shop', icon: Store },
  { id: 'financial', label: 'Financial', icon: DollarSign },
  { id: 'receipt', label: 'Receipt', icon: Receipt },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'staff', label: 'Staff', icon: UsersIcon },
  { id: 'system', label: 'System', icon: Settings2 },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 },
};

const colorSchemes = [
  { name: 'Indigo', primary: '#6366f1', bg: 'bg-indigo-500' },
  { name: 'Emerald', primary: '#10b981', bg: 'bg-emerald-500' },
  { name: 'Amber', primary: '#f59e0b', bg: 'bg-amber-500' },
  { name: 'Rose', primary: '#f43f5e', bg: 'bg-rose-500' },
  { name: 'Cyan', primary: '#06b6d4', bg: 'bg-cyan-500' },
  { name: 'Violet', primary: '#8b5cf6', bg: 'bg-violet-500' },
];

export default function Settings() {
  const dispatch = useDispatch();
  const { settings, shopInfo, systemInfo, loading } = useSelector((state) => state.settings);
  const { user, users } = useSelector((state) => state.auth);
  const { darkMode } = useSelector((state) => state.ui);

  const [activeTab, setActiveTab] = useState('shop');

  const [shopForm, setShopForm] = useState({ name: '', address: '', phone: '', email: '', businessHours: '', taxId: '' });
  const [logoPreview, setLogoPreview] = useState(null);

  const [financialForm, setFinancialForm] = useState({ defaultTaxRate: '', defaultDiscount: '', creditInterestRate: '', minTransaction: '', currency: 'PKR' });

  const [receiptForm, setReceiptForm] = useState({ showShopName: true, showLogo: true, footerText: '', returnPolicy: '', thankYouMessage: '', paperWidth: '80mm', autoPrint: false });

  const [notifForm, setNotifForm] = useState({ enabled: true, lowStockAlerts: true, creditAlerts: true, backupAlerts: true, emailNotifications: false });

  const [appearanceForm, setAppearanceForm] = useState({ fontSize: 'medium', colorScheme: 'indigo', sidebarDefault: false });

  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: '', email: '', role: 'staff', password: '' });

  useEffect(() => {
    dispatch(fetchSettings());
    dispatch(fetchShopInfo());
    dispatch(fetchSystemInfo());
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (shopInfo) {
      setShopForm({
        name: shopInfo.name || '',
        address: shopInfo.address || '',
        phone: shopInfo.phone || '',
        email: shopInfo.email || '',
        businessHours: shopInfo.businessHours || '',
        taxId: shopInfo.taxId || '',
      });
      setLogoPreview(shopInfo.logo || null);
    }
  }, [shopInfo]);

  useEffect(() => {
    if (settings) {
      setFinancialForm({
        defaultTaxRate: settings.defaultTaxRate || settings.taxRate || '',
        defaultDiscount: settings.defaultDiscount || settings.discount || '',
        creditInterestRate: settings.creditInterestRate || '',
        minTransaction: settings.minTransaction || '',
        currency: settings.currency || 'PKR',
      });
      setReceiptForm({
        showShopName: settings.showShopName !== false,
        showLogo: settings.showLogo !== false,
        footerText: settings.footerText || '',
        returnPolicy: settings.returnPolicy || '',
        thankYouMessage: settings.thankYouMessage || 'Thank you for your purchase!',
        paperWidth: settings.paperWidth || '80mm',
        autoPrint: settings.autoPrint || false,
      });
      setNotifForm({
        enabled: settings.notificationsEnabled !== false,
        lowStockAlerts: settings.lowStockAlerts !== false,
        creditAlerts: settings.creditAlerts !== false,
        backupAlerts: settings.backupAlerts !== false,
        emailNotifications: settings.emailNotifications || false,
      });
      setAppearanceForm({
        fontSize: settings.fontSize || 'medium',
        colorScheme: settings.colorScheme || 'indigo',
        sidebarDefault: settings.sidebarDefault || false,
      });
    }
  }, [settings]);

  const handleSaveShop = async () => {
    try {
      await dispatch(updateShop(shopForm)).unwrap();
      toast.success('Shop information saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save shop info');
    }
  };

  const handleSaveFinancial = async () => {
    try {
      await dispatch(updateFinancial(financialForm)).unwrap();
      toast.success('Financial settings saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save financial settings');
    }
  };

  const handleSaveReceipt = async () => {
    try {
      await dispatch(updateReceipt(receiptForm)).unwrap();
      toast.success('Receipt settings saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save receipt settings');
    }
  };

  const handleSaveNotifications = async () => {
    try {
      await dispatch(updateNotifications(notifForm)).unwrap();
      toast.success('Notification settings saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save notification settings');
    }
  };

  const handleSaveAppearance = async () => {
    try {
      await dispatch(updateAppearance(appearanceForm)).unwrap();
      if (darkMode !== (appearanceForm.colorScheme === 'dark')) {
        dispatch(setDarkMode(appearanceForm.colorScheme === 'dark'));
      }
      dispatch(setFontSize(appearanceForm.fontSize));
      toast.success('Appearance settings saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save appearance settings');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('logo', file);
      dispatch(uploadLogo(formData)).unwrap()
        .then(() => toast.success('Logo uploaded'))
        .catch((err) => toast.error(err?.message || 'Logo upload failed'));
    }
  };

  const handleStaffAction = async () => {
    try {
      if (staffForm.name && staffForm.email) {
        toast.success('Staff member added');
        setShowStaffModal(false);
        setStaffForm({ name: '', email: '', role: 'staff', password: '' });
        dispatch(getUsers());
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to manage staff');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'shop':
        return (
          <SettingsSection title="Shop Information" description="Manage your shop details" onSave={handleSaveShop} loading={loading}>
            <SettingRow
              label="Shop Name"
              description="Your business name"
              control={
                <input type="text" value={shopForm.name} onChange={(e) => setShopForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Shop Logo"
              description="Upload your logo"
              control={
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                    {logoPreview ? <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" /> : <Camera size={20} className="text-gray-400" />}
                  </div>
                  <label className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                    <Upload size={14} /> Upload
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                </div>
              }
            />
            <SettingRow
              label="Address"
              control={
                <input type="text" value={shopForm.address} onChange={(e) => setShopForm((p) => ({ ...p, address: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Phone"
              control={
                <input type="tel" value={shopForm.phone} onChange={(e) => setShopForm((p) => ({ ...p, phone: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Email"
              control={
                <input type="email" value={shopForm.email} onChange={(e) => setShopForm((p) => ({ ...p, email: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Business Hours"
              control={
                <input type="text" value={shopForm.businessHours} onChange={(e) => setShopForm((p) => ({ ...p, businessHours: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Tax ID"
              control={
                <input type="text" value={shopForm.taxId} onChange={(e) => setShopForm((p) => ({ ...p, taxId: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
          </SettingsSection>
        );

      case 'financial':
        return (
          <SettingsSection title="Financial Settings" description="Configure tax, discount, and currency" onSave={handleSaveFinancial} loading={loading}>
            <SettingRow
              label="Default Tax Rate (%)"
              control={
                <input type="number" min="0" max="100" step="0.1" value={financialForm.defaultTaxRate} onChange={(e) => setFinancialForm((p) => ({ ...p, defaultTaxRate: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Default Discount (%)"
              control={
                <input type="number" min="0" max="100" step="0.1" value={financialForm.defaultDiscount} onChange={(e) => setFinancialForm((p) => ({ ...p, defaultDiscount: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Credit Interest Rate (%)"
              control={
                <input type="number" min="0" step="0.1" value={financialForm.creditInterestRate} onChange={(e) => setFinancialForm((p) => ({ ...p, creditInterestRate: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Minimum Transaction Amount"
              control={
                <input type="number" min="0" step="0.01" value={financialForm.minTransaction} onChange={(e) => setFinancialForm((p) => ({ ...p, minTransaction: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              }
            />
            <SettingRow
              label="Currency"
              control={
                <select value={financialForm.currency} onChange={(e) => setFinancialForm((p) => ({ ...p, currency: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none">
                  <option value="PKR">PKR - Pakistani Rupee</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              }
            />
          </SettingsSection>
        );

      case 'receipt':
        return (
          <SettingsSection title="Receipt Settings" description="Customize your receipts" onSave={handleSaveReceipt} loading={loading}>
            <SettingRow
              label="Show Shop Name on Receipt"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={receiptForm.showShopName} onChange={(e) => setReceiptForm((p) => ({ ...p, showShopName: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
            <SettingRow
              label="Show Logo on Receipt"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={receiptForm.showLogo} onChange={(e) => setReceiptForm((p) => ({ ...p, showLogo: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
            <SettingRow
              label="Footer Text"
              control={
                <input type="text" value={receiptForm.footerText} onChange={(e) => setReceiptForm((p) => ({ ...p, footerText: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                  placeholder="Thank you for visiting!" />
              }
            />
            <SettingRow
              label="Return Policy"
              control={
                <textarea value={receiptForm.returnPolicy} onChange={(e) => setReceiptForm((p) => ({ ...p, returnPolicy: e.target.value }))} rows={2}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400 resize-none"
                  placeholder="No returns after 7 days" />
              }
            />
            <SettingRow
              label="Thank You Message"
              control={
                <input type="text" value={receiptForm.thankYouMessage} onChange={(e) => setReceiptForm((p) => ({ ...p, thankYouMessage: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                  placeholder="Thank you for your purchase!" />
              }
            />
            <SettingRow
              label="Paper Width"
              control={
                <select value={receiptForm.paperWidth} onChange={(e) => setReceiptForm((p) => ({ ...p, paperWidth: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none">
                  <option value="58mm">58mm</option>
                  <option value="80mm">80mm</option>
                  <option value="76mm">76mm</option>
                </select>
              }
            />
            <SettingRow
              label="Auto Print Receipt"
              description="Print receipt automatically after each sale"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={receiptForm.autoPrint} onChange={(e) => setReceiptForm((p) => ({ ...p, autoPrint: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
          </SettingsSection>
        );

      case 'notifications':
        return (
          <SettingsSection title="Notification Settings" description="Configure notifications and alerts" onSave={handleSaveNotifications} loading={loading}>
            <SettingRow
              label="Enable Notifications"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={notifForm.enabled} onChange={(e) => setNotifForm((p) => ({ ...p, enabled: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
            <SettingRow
              label="Low Stock Alerts"
              description="Get notified when products are running low"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={notifForm.lowStockAlerts} onChange={(e) => setNotifForm((p) => ({ ...p, lowStockAlerts: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
            <SettingRow
              label="Credit Alerts"
              description="Get notified about pending credit payments"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={notifForm.creditAlerts} onChange={(e) => setNotifForm((p) => ({ ...p, creditAlerts: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
            <SettingRow
              label="Backup Alerts"
              description="Get notified about backup status"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={notifForm.backupAlerts} onChange={(e) => setNotifForm((p) => ({ ...p, backupAlerts: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
            <SettingRow
              label="Email Notifications"
              description="Send notifications via email"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={notifForm.emailNotifications} onChange={(e) => setNotifForm((p) => ({ ...p, emailNotifications: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
          </SettingsSection>
        );

      case 'appearance':
        return (
          <SettingsSection title="Appearance Settings" description="Customize the look and feel" onSave={handleSaveAppearance} loading={loading}>
            <SettingRow
              label="Dark Mode"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={darkMode} onChange={(e) => dispatch(setDarkMode(e.target.checked))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
            <SettingRow
              label="Font Size"
              control={
                <select value={appearanceForm.fontSize} onChange={(e) => setAppearanceForm((p) => ({ ...p, fontSize: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none">
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              }
            />
            <SettingRow
              label="Color Scheme"
              control={
                <div className="flex gap-2">
                  {colorSchemes.map((scheme) => (
                    <button
                      key={scheme.name}
                      onClick={() => setAppearanceForm((p) => ({ ...p, colorScheme: scheme.name.toLowerCase() }))}
                      className={`w-8 h-8 rounded-full ${scheme.bg} flex items-center justify-center transition-all ${
                        appearanceForm.colorScheme === scheme.name.toLowerCase() ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-offset-gray-800 scale-110' : ''
                      }`}
                    >
                      {appearanceForm.colorScheme === scheme.name.toLowerCase() && <Check size={14} className="text-white" />}
                    </button>
                  ))}
                </div>
              }
            />
            <SettingRow
              label="Default Sidebar State"
              description="Start with sidebar collapsed"
              control={
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={appearanceForm.sidebarDefault} onChange={(e) => setAppearanceForm((p) => ({ ...p, sidebarDefault: e.target.checked }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              }
            />
          </SettingsSection>
        );

      case 'staff':
        return (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Staff Management</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Manage staff accounts and permissions</p>
                </div>
                <Button variant="primary" size="sm" icon={UserPlus} onClick={() => { setStaffForm({ name: '', email: '', role: 'staff', password: '' }); setShowStaffModal(true); }}>
                  Add Staff
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800/80">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Last Login</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">
                          <UsersIcon size={32} className="mx-auto mb-2" />
                          <p className="text-sm font-medium">No staff members</p>
                        </td>
                      </tr>
                    ) : (
                      users.map((staff, idx) => (
                        <tr key={staff._id || staff.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm">
                                {(staff.name || '?').charAt(0)}
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white">{staff.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-gray-600 dark:text-gray-400">{staff.email}</td>
                          <td className="px-6 py-3">
                            <Badge variant={staff.role === 'admin' ? 'primary' : 'default'} size="sm">
                              {(staff.role || 'staff').charAt(0).toUpperCase() + (staff.role || 'staff').slice(1)}
                            </Badge>
                          </td>
                          <td className="px-6 py-3">
                            <Badge variant={staff.active !== false ? 'success' : 'danger'} size="sm" dot>
                              {staff.active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-6 py-3 text-gray-500 dark:text-gray-400">
                            {staff.lastLogin ? new Date(staff.lastLogin).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => { toast.success(`Edit ${staff.name}`); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                                <Edit3 size={14} />
                              </button>
                              <button onClick={() => { toast.success(`${staff.name} removed`); }} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'system':
        return (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
                <Database size={18} className="text-gray-400" />
                System Information
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard icon={Activity} label="App Version" value={systemInfo?.version || '1.0.0'} color="blue" />
                <StatCard icon={Database} label="Database Size" value={systemInfo?.dbSize || '0 MB'} color="green" />
                <StatCard icon={ShoppingCart} label="Total Transactions" value={systemInfo?.totalTransactions || 0} color="purple" />
                <StatCard icon={Package} label="Total Products" value={systemInfo?.totalProducts || 0} color="yellow" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={UsersIcon} label="Total Customers" value={systemInfo?.totalCustomers || 0} color="cyan" />
                <StatCard icon={CreditCard} label="Total Revenue" value={systemInfo?.totalRevenue || 0} color="green" />
                <StatCard icon={Shield} label="Health Status" value={systemInfo?.health || 'Healthy'} color={systemInfo?.health === 'Healthy' ? 'green' : 'red'} />
                <StatCard icon={Clock} label="Uptime" value={systemInfo?.uptime || 'N/A'} color="purple" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">App Information</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400">Application Name</span>
                  <span className="font-medium text-gray-900 dark:text-white">Haider Pan Shop</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400">Version</span>
                  <span className="font-medium text-gray-900 dark:text-white">{systemInfo?.version || '1.0.0'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400">Platform</span>
                  <span className="font-medium text-gray-900 dark:text-white">{systemInfo?.platform || 'Web'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400">Node Environment</span>
                  <Badge variant="info" size="sm">{process.env.NODE_ENV || 'production'}</Badge>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-500 dark:text-gray-400">Database Status</span>
                  <Badge variant={systemInfo?.dbStatus === 'connected' ? 'success' : 'danger'} size="sm" dot>
                    {systemInfo?.dbStatus || 'Connected'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 md:p-6 space-y-6"
    >
      <PageHeader
        title="Settings"
        description="Configure your shop preferences"
      />

      <div className="flex overflow-x-auto gap-1 pb-1 scrollbar-thin">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {renderTabContent()}
        </motion.div>
      </AnimatePresence>

      <Modal
        isOpen={showStaffModal}
        onClose={() => setShowStaffModal(false)}
        size="sm"
        title="Add Staff Member"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Full Name</label>
            <input type="text" value={staffForm.name} onChange={(e) => setStaffForm((p) => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
              placeholder="Staff name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Email</label>
            <input type="email" value={staffForm.email} onChange={(e) => setStaffForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
              placeholder="email@example.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Role</label>
            <select value={staffForm.role} onChange={(e) => setStaffForm((p) => ({ ...p, role: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none">
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Password</label>
            <input type="password" value={staffForm.password} onChange={(e) => setStaffForm((p) => ({ ...p, password: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
              placeholder="Set password" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" fullWidth onClick={() => setShowStaffModal(false)}>Cancel</Button>
            <Button variant="primary" fullWidth onClick={handleStaffAction}>Add Staff</Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
